import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Check, Copy, Globe, Loader2, ShieldCheck } from 'lucide-react'
import type { EgressKindDto, EgressStatusDto, EgressVerificationDto } from '@apna/shared'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Static IPs — where broker traffic leaves from.
 *
 * Two jobs:
 *
 *   1. Tell you your current public IP, which is what a broker needs in order
 *      to whitelist you, and which nothing in the product could previously say.
 *
 *   2. Let you route ONLY broker calls through a VPS, so a development machine
 *      on a residential line can still reach an IP-whitelisted broker without
 *      tunnelling npm and git along with it.
 */

const KINDS: { value: EgressKindDto; label: string; hint: string }[] = [
  { value: 'DIRECT', label: 'Direct', hint: "This machine's own connection." },
  { value: 'SOCKS5', label: 'SOCKS5', hint: 'An ssh -D tunnel, or a SOCKS proxy on a VPS.' },
  { value: 'HTTP_PROXY', label: 'HTTP proxy', hint: 'tinyproxy or squid on a VPS.' },
]

const field =
  'h-9 rounded-lg border border-input bg-background px-2.5 text-sm text-foreground outline-none focus:border-ring'

export function StaticIpsTable() {
  const [status, setStatus] = useState<EgressStatusDto | null>(null)
  const [verification, setVerification] = useState<EgressVerificationDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const [kind, setKind] = useState<EgressKindDto>('DIRECT')
  const [host, setHost] = useState('127.0.0.1')
  const [port, setPort] = useState('1080')
  const [expected, setExpected] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const s = await api.get<EgressStatusDto>('/broker/egress')
      setStatus(s)
      setExpected(s.expectedIp ?? '')
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      // Saving verifies immediately — a saved-but-broken route is worse than
      // none, because it looks configured.
      const v = await api.put<EgressVerificationDto>('/broker/egress/config', {
        kind,
        host: kind === 'DIRECT' ? null : host.trim(),
        port: kind === 'DIRECT' ? null : Number(port) || null,
        expectedIp: expected.trim() || null,
      })
      setVerification(v)
      await load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const verify = async () => {
    setBusy(true)
    setError(null)
    try {
      setVerification(await api.post<EgressVerificationDto>('/broker/egress/verify'))
      await load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const copy = (value: string) => {
    void navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const effectiveIp = verification?.routedIp ?? status?.currentIp ?? null

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="flex items-start gap-3 border-b px-5 py-4">
          <div
            className="inline-flex shrink-0 items-center justify-center rounded-lg border bg-background"
            style={{ width: 36, height: 36 }}
          >
            <Globe className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground">Broker egress</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The address every broker call leaves from. Send it to your broker to whitelist.
            </p>
          </div>
          <button
            onClick={() => void verify()}
            disabled={busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            Verify route
          </button>
        </div>

        <div className="space-y-5 px-5 py-4">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Broker sees this IP
            </span>
            <div className="mt-1.5 flex items-center gap-2">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : effectiveIp ? (
                <>
                  <code className="num rounded-lg border bg-muted/40 px-3 py-1.5 text-sm font-semibold text-foreground">
                    {effectiveIp}
                  </code>
                  <button
                    onClick={() => copy(effectiveIp)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors hover:bg-accent"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  {verification?.routeWorks && (
                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-success">
                      via proxy
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Could not determine.</span>
              )}
            </div>
            {verification?.routeWorks && verification.directIp && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                This machine&apos;s own address is <code className="num">{verification.directIp}</code> —
                broker calls are leaving from the proxy instead.
              </p>
            )}
          </div>

          {/* ── route configuration ─────────────────────────────────────── */}
          <div className="rounded-lg border p-4">
            <h4 className="text-[13px] font-semibold text-foreground">Route broker calls through</h4>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              Only broker traffic is routed. Instrument sync, package installs and everything else
              keep using this machine&apos;s own connection.
            </p>

            <div className="mt-3 flex gap-2">
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  className={cn(
                    'flex-1 rounded-lg border px-2.5 py-2 text-left transition-colors',
                    kind === k.value ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                  )}
                >
                  <span className="block text-[11px] font-bold text-foreground">{k.label}</span>
                  <span className="mt-0.5 block text-[9.5px] leading-snug text-muted-foreground">
                    {k.hint}
                  </span>
                </button>
              ))}
            </div>

            {kind !== 'DIRECT' && (
              <div className="mt-3 flex items-end gap-2">
                <label className="block">
                  <span className="text-[11px] font-medium text-foreground">Host</span>
                  <input
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    spellCheck={false}
                    className={cn(field, 'num mt-1 w-44')}
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-foreground">Port</span>
                  <input
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    className={cn(field, 'num mt-1 w-24')}
                  />
                </label>
              </div>
            )}

            <div className="mt-3 flex items-end gap-2">
              <label className="block">
                <span className="text-[11px] font-medium text-foreground">
                  IP the broker whitelisted
                </span>
                <input
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  placeholder="optional"
                  spellCheck={false}
                  className={cn(field, 'num mt-1 w-56')}
                />
              </label>
              <button
                onClick={() => void save()}
                disabled={busy}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save &amp; verify
              </button>
            </div>

            {kind === 'SOCKS5' && (
              <div className="mt-3 rounded-lg border bg-muted/20 px-3 py-2.5">
                <p className="text-[11px] font-medium text-foreground">
                  Nothing to install on the VPS:
                </p>
                <code className="num mt-1.5 block text-[11px] text-muted-foreground">
                  ssh -N -D 1080 user@your-vps
                </code>
                <p className="mt-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
                  Opens a SOCKS5 proxy on <code className="num">127.0.0.1:1080</code> that exits
                  from the VPS. Leave it running; if it drops, Verify will tell you.
                </p>
              </div>
            )}
          </div>

          {verification && (
            <div
              className={cn(
                'flex items-start gap-2 rounded-lg border px-3 py-2.5',
                verification.error
                  ? 'border-destructive/30 bg-destructive/5'
                  : 'border-success/30 bg-success/5',
              )}
            >
              {verification.error ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              ) : (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              )}
              <div className="min-w-0 flex-1 text-xs leading-relaxed">
                {verification.error ? (
                  <p className="text-destructive">{verification.error}</p>
                ) : (
                  <p className="text-success">
                    Route confirmed. Broker calls leave from{' '}
                    <code className="num">{verification.routedIp}</code>
                    {verification.matchesExpected === true && ', which matches the whitelist.'}
                    {verification.matchesExpected === false &&
                      ` — but the broker expects ${verification.matchesExpected === false ? status?.expectedIp : ''}.`}
                  </p>
                )}
              </div>
            </div>
          )}

          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-5 shadow-sm">
        <h3 className="text-[13px] font-semibold text-foreground">
          Why a working login is not a working account
        </h3>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Measured against XTS on <span className="num">tradewiin.wiinance.com</span>, the same
          account and the same calls, before and after the broker whitelisted the egress IP:
        </p>
        <div className="mt-2 overflow-hidden rounded-lg border">
          <div className="flex items-center gap-3 border-b bg-muted/30 px-3 py-1.5">
            <span className="flex-1 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              Call
            </span>
            <span className="w-16 text-right text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              Not listed
            </span>
            <span className="w-16 text-right text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              Listed
            </span>
          </div>
          {[
            ['POST /interactive/user/session', '200', '200'],
            ['GET /interactive/user/balance', '400', '200'],
            ['GET /interactive/orders', '400', '200'],
            ['GET /interactive/portfolio/positions', '400', '200'],
            ['POST /interactive/orders/margindetails', '400', '400'],
          ].map(([path, before, after]) => (
            <div
              key={path}
              className="flex items-center gap-3 border-b px-3 py-1.5 last:border-0"
            >
              <code className="num flex-1 text-[10.5px] text-muted-foreground">{path}</code>
              {[before, after].map((code, i) => (
                <span key={i} className="w-16 text-right">
                  <span
                    className={cn(
                      'num rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      code === '200'
                        ? 'bg-success/10 text-success'
                        : 'bg-destructive/10 text-destructive',
                    )}
                  >
                    {code}
                  </span>
                </span>
              ))}
            </div>
          ))}
        </div>
        <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">
          The login endpoint accepts any address; every call made with the token is checked. That
          is why <span className="font-medium text-foreground">Test order capability</span> exists
          as a separate step — a green Login button says nothing about whether an order will place.
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">margindetails stays 400 either way.</span>{' '}
          It answers <code className="num">e-calculatemargin-0006 &quot;Data not found&quot;</code>{' '}
          for every input — this gateway does not run the margin service, so readiness falls back to
          reading the order book and reports itself as the weaker check it is.
        </p>
      </div>
    </div>
  )
}
